import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <main className="pt-20 pb-14 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-search-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">你访问的文章可能已被移除或不存在</p>
          <Link
            to="/article"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回文章列表
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    url: `https://butterbear.com/article/${article.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="pt-20 pb-14 md:pb-20">
        <article className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '精选文章', path: '/article' },
                  { label: article.title },
                ]}
              />
            </div>

            <div className="mb-6">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                {article.category}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 黄油小熊`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:my-4 prose-li:text-foreground-700 prose-li:mb-2
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="max-w-3xl mx-auto mt-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.date} · {ra.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
    </>
  );
}